// SegmentIngestionPipeline.ts
// Manages the active RAM window for incoming ticks and flushes completed segments to IndexedDB.
// Wraps around the feed ingestion to enable one-year replay storage.

import { action, makeObservable, observable, runInAction } from 'mobx';
import { TQuote } from 'src/types';
import { segmentIndexStore } from './SegmentIndexStore';
import {
    TickSegment,
    TickData,
    SegmentType,
    CompressionType,
    quoteToTickData,
    generateSegmentId,
    estimateSegmentSize,
} from './SegmentSchema';
import { CandleAggregator, GRANULARITY_PRESETS } from './CandleAggregator';

// Configuration constants
const DEFAULT_SEGMENT_SIZE_THRESHOLD = 10000; // Max ticks per segment
const DEFAULT_SEGMENT_TIME_THRESHOLD_MS = 60 * 60 * 1000; // 1 hour
const DEFAULT_RAM_WINDOW_SIZE = 50000; // Keep last 50K ticks in RAM
const DEFAULT_FLUSH_INTERVAL_MS = 5 * 60 * 1000; // Check for flush every 5 minutes

export interface IngestionConfig {
    segmentSizeThreshold?: number; // Max ticks/candles per segment
    segmentTimeThresholdMs?: number; // Max time span per segment
    ramWindowSize?: number; // Max ticks to keep in RAM
    flushIntervalMs?: number; // How often to check for segment rotation
    compression?: CompressionType;
    enableAutoFlush?: boolean;
}

interface ActiveWindow {
    symbol: string;
    granularity: number;
    startEpoch: number;
    ticks: TickData[];
    lastFlushCheck: number;
}

/**
 * SegmentIngestionPipeline: manages the active RAM window for incoming ticks,
 * rotates and flushes segments to IndexedDB when thresholds are met.
 */
export class SegmentIngestionPipeline {
    private _config: Required<IngestionConfig>;
    private _activeWindows: Map<string, ActiveWindow> = new Map(); // key: `${symbol}-${granularity}`
    private _flushTimer?: ReturnType<typeof setInterval>;

    // Observable stats for monitoring
    totalTicksIngested = 0;
    totalSegmentsFlushed = 0;
    lastFlushTime = 0;
    isRunning = false;

    constructor(config: IngestionConfig = {}) {
        this._config = {
            segmentSizeThreshold: config.segmentSizeThreshold ?? DEFAULT_SEGMENT_SIZE_THRESHOLD,
            segmentTimeThresholdMs: config.segmentTimeThresholdMs ?? DEFAULT_SEGMENT_TIME_THRESHOLD_MS,
            ramWindowSize: config.ramWindowSize ?? DEFAULT_RAM_WINDOW_SIZE,
            flushIntervalMs: config.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS,
            compression: config.compression ?? 'none',
            enableAutoFlush: config.enableAutoFlush ?? true,
        };

        makeObservable(this, {
            totalTicksIngested: observable,
            totalSegmentsFlushed: observable,
            lastFlushTime: observable,
            isRunning: observable,
            start: action,
            stop: action,
            ingest: action,
        });
    }

    /**
     * Start the ingestion pipeline (initialize segment index and auto-flush timer).
     */
    async start(): Promise<void> {
        if (this.isRunning) return;

        console.log('[SegmentIngestionPipeline] Starting...');
        await segmentIndexStore.initialize();

        if (this._config.enableAutoFlush) {
            this._flushTimer = setInterval(() => {
                this._checkAndFlushWindows();
            }, this._config.flushIntervalMs);
        }

        runInAction(() => {
            this.isRunning = true;
        });

        console.log('[SegmentIngestionPipeline] Started with config:', this._config);
    }

    /**
     * Stop the ingestion pipeline (flush pending segments and clear timer).
     */
    async stop(): Promise<void> {
        if (!this.isRunning) return;

        console.log('[SegmentIngestionPipeline] Stopping...');

        if (this._flushTimer) {
            clearInterval(this._flushTimer);
            this._flushTimer = undefined;
        }

        // Flush all pending windows
        await this._flushAllWindows();

        runInAction(() => {
            this.isRunning = false;
        });

        console.log('[SegmentIngestionPipeline] Stopped');
    }

    /**
     * Clear all pending segments without flushing to IndexedDB.
     * Used when aborting a chunked fetch to discard partial data.
     */
    clearPendingSegments(): void {
        const windowCount = this._activeWindows.size;
        const totalPendingTicks = Array.from(this._activeWindows.values())
            .reduce((sum, win) => sum + win.ticks.length, 0);

        this._activeWindows.clear();

        console.log('[SegmentIngestionPipeline] Cleared', windowCount, 'pending windows (',
            totalPendingTicks, 'ticks discarded)');
    }

    /**
     * Ingest a single quote (adds to active window, checks for rotation).
     */
    ingest(quote: TQuote, symbol: string, granularity: number = 0): void {
        if (!this.isRunning) {
            console.warn('[SegmentIngestionPipeline] Not running, ignoring quote');
            return;
        }

        const key = this._getWindowKey(symbol, granularity);
        let window = this._activeWindows.get(key);

        if (!window) {
            window = this._createWindow(symbol, granularity, quote);
            this._activeWindows.set(key, window);
        }

        // Convert quote to tick data
        const tickData = quoteToTickData(quote);
        window.ticks.push(tickData);

        runInAction(() => {
            this.totalTicksIngested++;
        });

        // Trim RAM if exceeding window size (but defer flush)
        this._trimRAMWindow(window);
        
        // Check rotation only every 1000 ticks to reduce flush frequency
        if (window.ticks.length % 1000 === 0) {
            this._checkWindowRotation(key, window);
        }
    }

    /**
     * Ingest a batch of quotes (historical data).
     * Async version that yields periodically to prevent UI blocking.
     */
    async ingestBatchAsync(quotes: TQuote[], symbol: string, granularity: number = 0): Promise<void> {
        if (!this.isRunning) {
            console.warn('[SegmentIngestionPipeline] Not running, ignoring batch');
            return;
        }

        const key = this._getWindowKey(symbol, granularity);
        let window = this._activeWindows.get(key);

        if (!window) {
            const firstQuote = quotes[0];
            window = this._createWindow(symbol, granularity, firstQuote);
            this._activeWindows.set(key, window);
        }

        // Process quotes in batches with yielding to prevent blocking
        const BATCH_SIZE = 500;
        for (let i = 0; i < quotes.length; i += BATCH_SIZE) {
            const batch = quotes.slice(i, i + BATCH_SIZE);
            
            for (const quote of batch) {
                const tickData = quoteToTickData(quote);
                window.ticks.push(tickData);
            }

            runInAction(() => {
                this.totalTicksIngested += batch.length;
            });

            // Trim RAM if exceeding window size (but don't flush yet)
            this._trimRAMWindow(window);

            // Yield to main thread every batch
            await Promise.resolve();
        }

        // After all quotes processed, check if window needs rotation
        this._checkWindowRotation(key, window);
    }

    /**
     * Ingest a batch of quotes (historical data).
     * Synchronous version for backward compatibility.
     */
    ingestBatch(quotes: TQuote[], symbol: string, granularity: number = 0): void {
        if (!this.isRunning) {
            console.warn('[SegmentIngestionPipeline] Not running, ignoring batch');
            return;
        }

        for (const quote of quotes) {
            this.ingest(quote, symbol, granularity);
        }
    }

    /**
     * Manually trigger flush for a specific symbol/granularity.
     */
    async flushSymbol(symbol: string, granularity: number = 0): Promise<void> {
        const key = this._getWindowKey(symbol, granularity);
        const window = this._activeWindows.get(key);
        if (window && window.ticks.length > 0) {
            await this._flushWindow(key, window);
        }
    }

    /**
     * Manually trigger flush for all active windows.
     */
    async flushAll(): Promise<void> {
        await this._flushAllWindows();
    }

    /**
     * Get current RAM window stats.
     */
    getWindowStats(): { symbol: string; granularity: number; tickCount: number; ageMs: number }[] {
        const stats = [];
        const now = Date.now();
        for (const [, window] of this._activeWindows.entries()) {
            stats.push({
                symbol: window.symbol,
                granularity: window.granularity,
                tickCount: window.ticks.length,
                ageMs: now - window.startEpoch,
            });
        }
        return stats;
    }

    // Private methods

    private _getWindowKey(symbol: string, granularity: number): string {
        return `${symbol}-${granularity}`;
    }

    private _createWindow(symbol: string, granularity: number, firstQuote: TQuote): ActiveWindow {
        const startEpoch = firstQuote.DT ? firstQuote.DT.getTime() : Date.parse(`${firstQuote.Date}Z`);
        return {
            symbol,
            granularity,
            startEpoch,
            ticks: [],
            lastFlushCheck: Date.now(),
        };
    }

    private _checkWindowRotation(key: string, window: ActiveWindow): void {
        const shouldRotate = this._shouldRotateWindow(window);
        if (shouldRotate) {
            // Flush current window as a segment
            this._flushWindow(key, window).then(() => {
                console.log('[SegmentIngestionPipeline] Rotated window:', key);
            }).catch((error) => {
                console.error('[SegmentIngestionPipeline] Failed to rotate window:', key, error);
            });
        }
    }

    private _shouldRotateWindow(window: ActiveWindow): boolean {
        const now = Date.now();
        const sizeExceeded = window.ticks.length >= this._config.segmentSizeThreshold;
        const timeExceeded = (now - window.startEpoch) >= this._config.segmentTimeThresholdMs;
        return sizeExceeded || timeExceeded;
    }

    private _trimRAMWindow(window: ActiveWindow): void {
        if (window.ticks.length > this._config.ramWindowSize) {
            const excessCount = window.ticks.length - this._config.ramWindowSize;
            window.ticks.splice(0, excessCount);
            console.log('[SegmentIngestionPipeline] Trimmed RAM window:', window.symbol, 'removed:', excessCount);
        }
    }

    private async _checkAndFlushWindows(): Promise<void> {
        const now = Date.now();
        const promises: Promise<void>[] = [];

        for (const [key, window] of this._activeWindows.entries()) {
            // Only check windows that haven't been checked recently
            if (now - window.lastFlushCheck > this._config.flushIntervalMs) {
                window.lastFlushCheck = now;
                if (this._shouldRotateWindow(window)) {
                    promises.push(this._flushWindow(key, window));
                }
            }
        }

        if (promises.length > 0) {
            await Promise.all(promises);
        }
    }

    private async _flushWindow(_key: string, window: ActiveWindow): Promise<void> {
        if (window.ticks.length === 0) return;

        const endEpoch = window.ticks[window.ticks.length - 1].epoch;
        const segmentId = generateSegmentId(window.symbol, window.granularity, window.startEpoch);

        // Apply optional compression
        let ticks = window.ticks;
        let compression = this._config.compression;
        if (compression === 'delta') {
            ticks = this._applyDeltaCompression(ticks);
        }

        // Create tick segment
        const segment: TickSegment = {
            metadata: {
                segmentId,
                symbol: window.symbol,
                granularity: window.granularity,
                type: 'tick' as SegmentType,
                startEpoch: window.startEpoch,
                endEpoch,
                count: ticks.length,
                sizeBytes: estimateSegmentSize(ticks.length, 'tick'),
                compression,
                createdAt: Date.now(),
            },
            ticks,
        };

        // Persist tick segment to IndexedDB
        await segmentIndexStore.addSegmentToIndex(segment);

        // Auto-generate candle aggregates for this tick segment
        await this._generateCandleAggregates(window.symbol, window.ticks);

        runInAction(() => {
            this.totalSegmentsFlushed++;
            this.lastFlushTime = Date.now();
        });

        // Reset window for new segment (keep recent ticks if within RAM window size)
        const keepCount = Math.min(window.ticks.length, Math.floor(this._config.ramWindowSize / 2));
        const lastEpoch = window.ticks[window.ticks.length - 1].epoch;
        window.ticks = window.ticks.slice(-keepCount);
        window.startEpoch = lastEpoch + 1;
        window.lastFlushCheck = Date.now();

        console.log('[SegmentIngestionPipeline] Flushed segment:', segmentId, 'count:', segment.metadata.count);
    }

    private async _flushAllWindows(): Promise<void> {
        const promises: Promise<void>[] = [];
        for (const [key, window] of this._activeWindows.entries()) {
            if (window.ticks.length > 0) {
                promises.push(this._flushWindow(key, window));
            }
        }
        if (promises.length > 0) {
            await Promise.all(promises);
        }
    }

    private _applyDeltaCompression(ticks: TickData[]): TickData[] {
        if (ticks.length === 0) return ticks;

        const compressed: TickData[] = [];
        let lastPrice = 0;

        for (const tick of ticks) {
            const delta = lastPrice === 0 ? tick.price : tick.price - lastPrice;
            compressed.push({
                epoch: tick.epoch,
                price: delta,
                volume: tick.volume,
            });
            lastPrice = tick.price;
        }

        return compressed;
    }

    /**
     * Apply delta compression to ticks (for background compaction).
     */
    static applyDeltaCompression(ticks: TickData[]): TickData[] {
        if (ticks.length === 0) return ticks;

        const compressed: TickData[] = [];
        let lastPrice = 0;

        for (const tick of ticks) {
            const delta = lastPrice === 0 ? tick.price : tick.price - lastPrice;
            compressed.push({
                epoch: tick.epoch,
                price: delta,
                volume: tick.volume,
            });
            lastPrice = tick.price;
        }

        return compressed;
    }

    /**
     * Decompress delta-encoded ticks (for reading back from storage).
     */
    static decompressDeltaTicks(ticks: TickData[]): TickData[] {
        if (ticks.length === 0) return ticks;

        const decompressed: TickData[] = [];
        let lastPrice = 0;

        for (const tick of ticks) {
            const actualPrice = lastPrice === 0 ? tick.price : lastPrice + tick.price;
            decompressed.push({
                epoch: tick.epoch,
                price: actualPrice,
                volume: tick.volume,
            });
            lastPrice = actualPrice;
        }

        return decompressed;
    }

    /**
     * Auto-generate candle aggregates for tick segment.
     * Creates 1m, 5m, 15m, 1h candle segments and persists to IndexedDB.
     */
    private async _generateCandleAggregates(
        symbol: string,
        ticks: TickData[]
    ): Promise<void> {
        if (ticks.length === 0) return;

        // Define granularities to pre-aggregate
        const targetGranularities = [
            GRANULARITY_PRESETS.ONE_MINUTE,
            GRANULARITY_PRESETS.FIVE_MINUTES,
            GRANULARITY_PRESETS.FIFTEEN_MINUTES,
            GRANULARITY_PRESETS.ONE_HOUR,
        ];

        const aggregationPromises = targetGranularities.map(async (granularity) => {
            const aggregator = new CandleAggregator({
                granularitySeconds: granularity,
                symbol,
            });

            const candles = aggregator.aggregateTicks(ticks);
            
            if (candles.length > 0) {
                const candleSegment = CandleAggregator.createCandleSegment(candles, symbol, granularity);
                await segmentIndexStore.addSegmentToIndex(candleSegment);
                console.log(`[SegmentIngestionPipeline] Generated ${candles.length} ${granularity}s candles for ${symbol}`);
            }
        });

        await Promise.all(aggregationPromises);
    }
}

// Singleton instance
export const segmentIngestionPipeline = new SegmentIngestionPipeline();
